import { useEffect, useMemo, useRef, useState } from "react";

import type { SimklClient } from "@/api/client";
import type { LibraryShow } from "@/api/schemas";
import { buildSeasons } from "@/lib/seasons";
import { Foot } from "@/screens/Show/ShowFoot";
import { Head, Overview, Stats } from "@/screens/Show/ShowHead";
import { Body, buildActions } from "@/screens/Show/SheetBody";
import type { OutboxAction } from "@/store/outbox";
import { useSheetData } from "@/store/useSheetData";
import { useDragToClose } from "@/screens/Show/useDragToClose";

const NO_SEASONS: ReadonlySet<number> = new Set();

/**
 * Which season drawers are open.
 *
 * Every show opens with all of them collapsed. Auto-opening the first
 * unfinished season saved a tap but meant the sheet's contents differed from
 * show to show, and the list jumped as episodes arrived and changed which
 * season qualified.
 *
 * Null means "the user has not chosen yet", which is what lets the default be
 * derived during render instead of written back from an effect. The choice is
 * tagged with the show, so one made on one show does not decide which season
 * opens on the next.
 */
function useOpenSeasons(simklId: number | null) {
  const [chosen, setChosen] = useState<{ simklId: number; seasons: ReadonlySet<number> } | null>(
    null,
  );
  const openSeasons = useMemo(
    () => (chosen !== null && chosen.simklId === simklId ? chosen.seasons : NO_SEASONS),
    [chosen, simklId],
  );
  return { openSeasons, setChosen };
}

/** Escape closes the sheet, for the desktop browser the app is built in. */
function useEscape(active: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [active, onClose]);
}

export function ShowSheet({
  show,
  inLibrary,
  pending,
  setAside,
  client,
  onAction,
  onAdd,
  onToggleAside,
  onClose,
}: {
  show: LibraryShow | null;
  /** False for a search result being previewed before it is tracked. */
  inLibrary: boolean;
  /** True while a write for this show has not reached Simkl yet. */
  pending: boolean;
  /** Set aside: kept in the library, moved out of the main queue. */
  setAside: boolean;
  onToggleAside: () => void;
  client: SimklClient;
  onAction: (action: OutboxAction) => void;
  onAdd: () => void;
  onClose: () => void;
}) {
  const simklId = show?.show.ids.simkl ?? null;
  const { episodes, loading, error, detail } = useSheetData(client, simklId);
  const { openSeasons, setChosen } = useOpenSeasons(simklId);
  const body = useRef<HTMLDivElement>(null);
  const { offset, sheet, handlers } = useDragToClose(onClose, body);

  const seasons = useMemo(
    () => (show === null ? [] : buildSeasons(show, episodes)),
    [show, episodes],
  );

  useEscape(show !== null, onClose);

  const open = show !== null;

  return (
    <>
      <div className={open ? "scrim open" : "scrim"} onClick={onClose} aria-hidden="true" />
      <div
        className={offset === null ? (open ? "sheet open" : "sheet") : "sheet open dragging"}
        style={offset === null ? undefined : { transform: `translateY(${String(offset)}px)` }}
        ref={sheet}
        role="dialog"
        aria-modal="true"
        aria-label={show?.show.title ?? "Show details"}
        {...handlers}
      >
        <div className="sheet-grip">
          <div className="grab" />
          {show !== null && <Head show={show} seasons={seasons} detail={detail} />}
        </div>
        <div className="sheet-body" ref={body}>
          {show !== null && <Stats show={show} detail={detail} />}
          {detail !== null && detail.overview !== null && <Overview text={detail.overview} />}
          <Body
            loading={loading}
            error={error}
            seasons={seasons}
            openSeasons={openSeasons}
            pending={pending}
            onToggleSeason={(number) => {
              const next = new Set(openSeasons);
              if (!next.delete(number)) next.add(number);
              if (simklId !== null) setChosen({ simklId, seasons: next });
            }}
            actions={buildActions(simklId, seasons, onAction)}
          />
          {show !== null && (
            <Foot
              show={show}
              inLibrary={inLibrary}
              setAside={setAside}
              onAction={onAction}
              onAdd={onAdd}
              onToggleAside={onToggleAside}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </>
  );
}
